import { Event, TicketType } from "../types/EventPaymentTypes";

export interface UITicket {
  ticket_id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  quantity_total?: number;
  quantity_sold?: number;
  min_per_order?: number;
  max_per_order?: number;
  show_remaining_tickets?: boolean;
  sale_start_date?: string;
  sale_end_date?: string;
  group_identification?: string;
}

export interface UIEventForm {
  event_name: string;
  event_description?: string;
  location?: string;
  event_image_url?: string;
  ticket_types: UITicket[];
  active: boolean;
}

export const eventToUIForm = (event: Event | undefined | null): UIEventForm => {
  if (!event || !event.event_data) {
    return createEmptyUIForm();
  }

  const ticketTypesArray: UITicket[] = Object.entries(
    event.event_data.ticket_types || {}
  ).map(([ticket_id, ticket]) => ({
    ticket_id,
    name: ticket.settings.name,
    description: ticket.settings.description,
    price: ticket.settings.price,
    currency: ticket.settings.currency,
    quantity_total: ticket.settings.quantity_total,
    quantity_sold: event.event_data.sold?.[ticket_id] || 0,
    min_per_order: ticket.settings.min_per_order,
    max_per_order: ticket.settings.max_per_order,
    show_remaining_tickets: ticket.settings.show_remaining_tickets,
    sale_start_date: ticket.settings.sale_start_date,
    sale_end_date: ticket.settings.sale_end_date,
    group_identification: ticket.settings.group_identification,
  }));

  return {
    event_name: event.event_data.settings?.event_name || "",
    event_description: event.event_data.settings?.event_description,
    location: event.event_data.settings?.location,
    event_image_url: event.event_data.settings?.event_image_url,
    ticket_types: ticketTypesArray,
    active: event.active !== false,
  };
};

export const uiFormToEventPayload = (form: UIEventForm) => {
  const ticketTypesObject: { [ticket_id: string]: TicketType } = {};

  form.ticket_types.forEach((ticket) => {
    const ticketId =
      ticket.ticket_id ||
      `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    ticketTypesObject[ticketId] = {
      settings: {
        name: ticket.name,
        description: ticket.description,
        price: ticket.price,
        currency: ticket.currency,
        quantity_total: ticket.quantity_total,
        min_per_order: ticket.min_per_order,
        max_per_order: ticket.max_per_order,
        show_remaining_tickets: ticket.show_remaining_tickets,
        sale_start_date: ticket.sale_start_date,
        sale_end_date: ticket.sale_end_date,
        group_identification: ticket.group_identification,
      },
    };
  });

  return {
    active: form.active,
    event_data: {
      settings: {
        event_name: form.event_name,
        event_description: form.event_description,
        location: form.location,
        event_image_url: form.event_image_url,
      },
      ticket_types: ticketTypesObject,
    },
  };
};

export const createEmptyUIForm = (): UIEventForm => ({
  event_name: "",
  event_description: "",
  location: "",
  event_image_url: "",
  ticket_types: [],
  active: true,
});

export const createEmptyUITicket = (): Partial<UITicket> => ({
  ticket_id: "",
  name: "",
  description: "",
  price: 0,
  currency: "USD",
  quantity_total: undefined,
  min_per_order: 1,
  max_per_order: 2,
  show_remaining_tickets: true,
  sale_start_date: "",
  sale_end_date: "",
  group_identification: "",
});

export const utcToLocalDatetime = (utcString: string | undefined): string => {
  if (!utcString) return "";
  const date = new Date(utcString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const localDatetimeToUtc = (localDatetime: string): string => {
  if (!localDatetime) return "";
  const date = new Date(localDatetime);
  return date.toISOString();
};
